import { useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FileStack, History, Clock, AlertTriangle, Eye } from 'lucide-react'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { useDocuments } from '@/lib/hooks/use-documents'
import type { DocumentInstanceDto } from '@/lib/api/services/documents'

export default function DocumentsPage() {
  const navigate = useNavigate()

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
    getODataParams,
  } = useDataTable({
    initialState: {
      pageSize: 20,
      sortField: 'name',
      sortDirection: 'asc',
    },
  })

  const params = getODataParams()

  const { data, isLoading, isError, refetch } = useDocuments(params)

  const documents = data?.value ?? []
  const totalCount = data?.['@odata.count'] ?? documents.length

  const handleRetry = useCallback(() => {
    refetch()
  }, [refetch])

  const columns: ColumnDef<DocumentInstanceDto>[] = [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      cell: (doc) => (
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
            <FileStack className="h-4 w-4 text-white" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{doc.name}</span>
        </div>
      ),
    },
    {
      id: 'templateName',
      header: 'Template',
      accessor: 'templateName',
      sortable: true,
      filterable: true,
      filterType: 'text',
      cell: (doc) => (
        <span className="text-gray-700 dark:text-gray-300">{doc.templateName}</span>
      ),
    },
    {
      id: 'documentVersion',
      header: 'Document Version',
      accessor: 'documentVersion',
      sortable: true,
      width: '150px',
      cell: (doc) => (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          <History className="h-3 w-3" />
          v{doc.documentVersion}
        </span>
      ),
    },
    {
      id: 'lastSyncedAt',
      header: 'Last Synced',
      accessor: 'lastSyncedAt',
      sortable: true,
      cell: (doc) => (
        <span className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
          <Clock className="h-3.5 w-3.5" />
          {doc.lastSyncedAt ? new Date(doc.lastSyncedAt).toLocaleString() : 'Never'}
        </span>
      ),
    },
    {
      id: 'fieldCount',
      header: 'Fields',
      accessor: 'fieldCount',
      sortable: true,
      width: '100px',
      cell: (doc) => (
        <div className="flex items-center gap-2">
          <span className="text-gray-700 dark:text-gray-300">{doc.fieldCount}</span>
          {doc.pendingDeleteCount > 0 && (
            <Badge variant="warning" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              {doc.pendingDeleteCount} pending delete
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: 'Active' },
        { label: 'Archived', value: 'Archived' },
      ],
      cell: (doc) => (
        <span
          className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
            doc.status === 'Active'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
          }`}
        >
          {doc.status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      accessor: 'id',
      width: '80px',
      cell: (doc) => (
        <Link
          to={`/documents/${doc.id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <Eye className="h-3.5 w-3.5" />
          View
        </Link>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documents"
        description="Word documents created from templates and synced with content controls"
      />

      <FluentCard className="overflow-hidden">
        <DataTable
          columns={columns}
          data={documents}
          totalCount={totalCount}
          loading={isLoading}
          error={isError ? 'Failed to load documents' : null}
          onRetry={handleRetry}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={setSort}
          filters={filters}
          onFilterChange={setFilters}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search documents..."
          rowKey="id"
          onRowClick={(doc) => navigate(`/documents/${doc.id}`)}
          emptyMessage="No documents found. Create a document from a template in the Word add-in to get started."
        />
      </FluentCard>
    </div>
  )
}
